/**
 * Priority hooks for React Query
 *
 * Provides data fetching and mutation hooks for priority management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as priorityApi from '@/api/priority';
import { taskKeys } from './use-tasks';
import type { Task } from '@/types';

// Query keys
export const priorityKeys = {
  all: ['priority'] as const,
  health: () => [...priorityKeys.all, 'health'] as const,
  history: (taskId: string) => [...priorityKeys.all, 'history', taskId] as const,
};

// Type for task list response
interface TaskListResponse {
  tasks: Task[];
  total: number;
  hasMore: boolean;
}

/**
 * Hook for checking priority engine health
 */
export function usePriorityHealth() {
  return useQuery({
    queryKey: priorityKeys.health(),
    queryFn: () => priorityApi.healthCheck(),
    staleTime: 60 * 1000, // Consider stale after 1 minute
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * Hook for getting priority history
 */
export function usePriorityHistory(taskId: string, limit?: number) {
  return useQuery({
    queryKey: priorityKeys.history(taskId),
    queryFn: () => priorityApi.getPriorityHistory(taskId, limit),
    enabled: Boolean(taskId),
    staleTime: 30 * 1000, // Consider stale after 30 seconds
  });
}

/**
 * Hook for priority mutations
 */
export function usePriorityMutations() {
  const queryClient = useQueryClient();

  // Invalidate priority and task queries after mutations
  const invalidateQueries = (taskId?: string) => {
    queryClient.invalidateQueries({ queryKey: priorityKeys.all });
    queryClient.invalidateQueries({ queryKey: taskKeys.all });
    if (taskId) {
      queryClient.invalidateQueries({ queryKey: priorityKeys.history(taskId) });
    }
  };

  // Recalculate priority
  const recalculate = useMutation({
    mutationFn: (taskId: string) => priorityApi.recalculatePriority(taskId),
    onSuccess: (_, taskId) => {
      invalidateQueries(taskId);
    },
  });

  // Helper to optimistically update task in all cached lists
  const updateTaskInCache = (
    taskId: string, 
    updates: Partial<Pick<Task, 'priorityScore' | 'isPinned' | 'needsReview'>>
  ) => {
    // Get all task list queries and update them - use predicate for broader matching
    const cache = queryClient.getQueryCache();
    const queries = cache.findAll({ queryKey: ['tasks'] });
    
    queries.forEach((query) => {
      const data = query.state.data as TaskListResponse | undefined;
      if (data?.tasks) {
        queryClient.setQueryData(query.queryKey, {
          ...data,
          tasks: data.tasks.map((task) =>
            task.id === taskId ? { ...task, ...updates } : task
          ),
        });
      }
    });
  };

  // Touch task (reset decay, optionally bump)
  const touch = useMutation({
    mutationFn: ({ taskId, bumpAmount }: { taskId: string; bumpAmount?: number }) =>
      priorityApi.touchTask(taskId, bumpAmount),
    onMutate: async ({ taskId, bumpAmount }) => {
      // Cancel all outgoing task queries
      await queryClient.cancelQueries({ queryKey: ['tasks'] });
      
      // Snapshot current cache for rollback
      const cache = queryClient.getQueryCache();
      const queries = cache.findAll({ queryKey: ['tasks'] });
      const previousData: Array<[readonly unknown[], TaskListResponse | undefined]> = 
        queries.map(q => [q.queryKey, q.state.data as TaskListResponse | undefined]);
      
      // Optimistically update the score
      if (bumpAmount !== undefined) {
        queries.forEach((query) => {
          const data = query.state.data as TaskListResponse | undefined;
          if (data?.tasks) {
            queryClient.setQueryData(query.queryKey, {
              ...data,
              tasks: data.tasks.map((task) => {
                if (task.id !== taskId) return task;
                const currentScore = task.priorityScore ?? 50;
                const newScore = Math.max(0, Math.min(100, currentScore + bumpAmount));
                return { ...task, priorityScore: newScore, needsReview: false };
              }),
            });
          }
        });
      } else {
        updateTaskInCache(taskId, { needsReview: false });
      }
      
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          if (data) queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (_, __, { taskId }) => {
      // Refetch to ensure consistency
      invalidateQueries(taskId);
    },
  });

  // Pin task
  const pin = useMutation({
    mutationFn: ({ taskId, reason, days }: { taskId: string; reason: string; days?: number }) =>
      priorityApi.pinTask(taskId, reason, days),
    onMutate: async ({ taskId }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks'] });
      
      const cache = queryClient.getQueryCache();
      const queries = cache.findAll({ queryKey: ['tasks'] });
      const previousData: Array<[readonly unknown[], TaskListResponse | undefined]> = 
        queries.map(q => [q.queryKey, q.state.data as TaskListResponse | undefined]);
      
      // Optimistically set isPinned = true
      updateTaskInCache(taskId, { isPinned: true });
      
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          if (data) queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (_, __, { taskId }) => {
      invalidateQueries(taskId);
    },
  });

  // Unpin task
  const unpin = useMutation({
    mutationFn: (taskId: string) => priorityApi.unpinTask(taskId),
    onMutate: async (taskId) => {
      await queryClient.cancelQueries({ queryKey: ['tasks'] });
      
      const cache = queryClient.getQueryCache();
      const queries = cache.findAll({ queryKey: ['tasks'] });
      const previousData: Array<[readonly unknown[], TaskListResponse | undefined]> = 
        queries.map(q => [q.queryKey, q.state.data as TaskListResponse | undefined]);
      
      // Optimistically set isPinned = false
      updateTaskInCache(taskId, { isPinned: false });
      
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          if (data) queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (_data, _error, taskId) => {
      invalidateQueries(taskId);
    },
  });

  // Trigger daily batch (admin)
  const triggerBatch = useMutation({
    mutationFn: (individualId?: string) => priorityApi.triggerDailyBatch(individualId),
    onSuccess: () => {
      invalidateQueries();
    },
  });

  return {
    recalculate,
    touch,
    pin,
    unpin,
    triggerBatch,
  };
}

/**
 * Get priority score color based on value
 */
export function getPriorityColor(score: number): string {
  if (score >= 90) return 'text-red-600 bg-red-50';
  if (score >= 70) return 'text-orange-600 bg-orange-50';
  if (score >= 50) return 'text-yellow-600 bg-yellow-50';
  if (score >= 30) return 'text-blue-600 bg-blue-50';
  if (score >= 10) return 'text-gray-600 bg-gray-50';
  return 'text-gray-400 bg-gray-50';
}

/**
 * Get priority label based on score
 */
export function getPriorityLabel(score: number): string {
  if (score >= 90) return 'Critical';
  if (score >= 70) return 'High';
  if (score >= 50) return 'Medium';
  if (score >= 30) return 'Low';
  if (score >= 10) return 'Backlog';
  return 'Dormant';
}

